<template>
  <FusionBaseDialog
    v-bind="$attrs"
    :content-class="dialogContentClass()"
    :open="model"
    :error="error"
    :icon="icon"
    :max-height="getSafeHeight(maxHeight)"
    :max-width="getSafeWidth(maxWidth)"
    :no-actions="noActions"
    :no-header="noHeader"
    :persistent-overlay="persistent || persistentOverlay"
    :hide-overlay="!scrim"
    :subtitle="subTitle"
    :title="title"
    @close="close"
  >
    <template v-if="$slots.header" v-slot:header>
      <slot name="header" />
    </template>
    <template v-else-if="$slots.status" v-slot:header>
      <v-card-item>
        <template v-if="icon" v-slot:prepend>
          <v-icon :color="iconColor" :icon="icon" />
        </template>
        <template v-if="title" v-slot:title>{{ title }}</template>
        <template v-if="subTitle" v-slot:subtitle>{{ subTitle }}</template>
        <template v-slot:append>
          <slot name="status" />
        </template>
      </v-card-item>
    </template>

    <div :style="contentStyle">
      <slot />
    </div>

    <template v-slot:actions>
      <slot name="actions">
        <v-spacer />
        <ButtonPrimary @click="close" :text="t('buttons.close')" />
      </slot>
    </template>
  </FusionBaseDialog>
</template>

<script setup lang="ts">
import { computed, useAttrs } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay, type DisplayInstance } from 'vuetify'

defineOptions({ inheritAttrs: false })

const { t } = useI18n({ useScope: 'global' })
const display: DisplayInstance = useDisplay()
const attrs = useAttrs()

const props = defineProps({
  error: { type: String, default: undefined },
  icon: { type: String, default: undefined },
  iconColor: { type: String, default: undefined },
  minHeight: { type: Number, default: undefined },
  minWidth: { type: Number, default: undefined },
  maxHeight: { type: [Number, String], default: undefined },
  maxWidth: { type: [Number, String], default: undefined },
  noHeader: { type: Boolean, default: false },
  noActions: { type: Boolean, default: false },
  persistent: { type: Boolean, default: true },
  persistentOverlay: { type: Boolean, default: false },
  scrim: { type: Boolean, default: true },
  title: { type: String, default: undefined },
  subTitle: { type: String, default: undefined }
})

const model = defineModel<boolean>({ default: false })
const emit = defineEmits<{ close: [] }>()

const dialogContentClass = () =>
  [
    'fitpro-dialog-baseDialog',
    typeof attrs['data-test-id'] === 'string' ? `fitpro-dialog-${attrs['data-test-id']}` : '',
    typeof attrs['content-class'] === 'string' ? attrs['content-class'] : '',
    typeof attrs.contentClass === 'string' ? attrs.contentClass : ''
  ]
    .filter(Boolean)
    .join(' ')

const contentStyle = computed(() => ({
  minHeight: toPixels(getSafeHeight(props.minHeight)),
  minWidth: toPixels(getSafeWidth(props.minWidth))
}))

const close = () => {
  model.value = false
  emit('close')
}

const getSafeHeight = (value?: number | string): number | string | undefined =>
  typeof value === 'number' ? Math.min(value, display.height.value) : value

const getSafeWidth = (value?: number | string): number | string | undefined =>
  typeof value === 'number' ? Math.min(value, display.width.value) : value

const toPixels = (value?: number | string) => (typeof value === 'number' ? `${value}px` : value)
</script>
